import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, Button, TextField, Typography } from '@mui/material';
import { Row, Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { Load } from '../../../../types';
import { filterStopByDestination } from '../../../../utils/data-mapping-utils';
import { useWeights } from '../../../shared/hooks/use-weights';
import { Select } from '../../../shared/inputs/select';
import { GlobalFilter, GlobalFilterItem, GlobalFilterObject } from '../../hooks/use-draft-load-table';
import { globalFilterRemover } from '../../utils/drafts-loads-utils';

export type ComparisonFilter = GlobalFilterObject<'comparisonFilter', ComparisonFilterObject>;

export type ComparisonFilterObject = {
    comparator: string;
    weight: number;
}

export const Comparison = {
    LessThan: 'less-than',
    LessThanEqual: 'less-than-equal',
    MoreThan: 'more-than',
    MoreThanEqual: 'more-than-equal',
    Equal: 'equal',
};

const COMPARISON_OPERATORS = [
    { id: Comparison.LessThan, label: '<' },
    { id: Comparison.LessThanEqual, label: '<=' },
    { id: Comparison.MoreThan, label: '>' },
    { id: Comparison.MoreThanEqual, label: '>=' },
    { id: Comparison.Equal, label: '=' },
];

interface ComparisonFilterProps {
    table: Table<Load>;
}

export const ComparisonFilter: React.FC<ComparisonFilterProps> = (props) => {
    const { table } = props;
    const { weightAbbreviation, convertFromGrams } = useWeights();
    const globalFilter = table.getState().globalFilter as GlobalFilter;
    const activeComparisonFilter = globalFilter?.find((filterItem: GlobalFilterItem) => filterItem.type === 'comparisonFilter');
    const defaultOperator = COMPARISON_OPERATORS[0];
    
    const [search, setSearch] = React.useState(activeComparisonFilter?.value.weight || '');
    
    function comparisonFilterFn(row: Row<Load>, value: ComparisonFilterObject) {
        const load = row.original;
        const operator = value.comparator;
        const destinationsWithWeight = load.stops.filter(stop => filterStopByDestination(stop) && stop.weight !== null);

        if (destinationsWithWeight.length === 0) {
            return false;
        } else {
            const loadDestinationWeightTotal = destinationsWithWeight
                .map(stop => stop.weight || 0)
                .reduce((acc, stopWeight) => acc + stopWeight);

            const totalWeightForCompare = convertFromGrams(loadDestinationWeightTotal);

            if (operator === Comparison.LessThan) {
                return value.weight > totalWeightForCompare;
            } else if (operator === Comparison.LessThanEqual) {
                return value.weight >= totalWeightForCompare;
            } else if (operator === Comparison.MoreThan) {
                return value.weight < totalWeightForCompare;
            } else if (operator === Comparison.MoreThanEqual) {
                return value.weight <= totalWeightForCompare;
            } else if (operator === Comparison.Equal) {
                return value.weight === totalWeightForCompare;
            }

            // this is impossible, but TS does not like an undef
            return true;
        }
    }

    function createComparisonFilter(comparator: string, weight: number): ComparisonFilter {
        const comparatorLabel = COMPARISON_OPERATORS.find(op => op.id === comparator)?.label || '';
        return {
            type: 'comparisonFilter',
            label: `${comparatorLabel} ${weight}`,
            value: {
                comparator,
                weight,
            },
            filterFn: comparisonFilterFn,
        }
    }

    function handleCategoryChange(item: typeof COMPARISON_OPERATORS[number]) {
        table.setGlobalFilter((prevGlobalFilter: GlobalFilter): GlobalFilter => {
            const globalFilterWithoutComparisonFilter = prevGlobalFilter?.filter(i => i.type !== 'comparisonFilter');
            const prevWeight = prevGlobalFilter?.find(f => f.type === 'comparisonFilter')?.value.weight;

            const newComparisonFilter = createComparisonFilter(item.id, prevWeight || 0);
            return [...(globalFilterWithoutComparisonFilter || []), newComparisonFilter];
        });
    }

    const updateFilterForWeight = React.useCallback(
        debounce((weight: number) => {
            table.setGlobalFilter((prevGlobalFilter: GlobalFilter): GlobalFilter => {
                const globalFilterWithoutComparisonFilter = prevGlobalFilter?.filter(i => i.type !== 'comparisonFilter');
                const prevComparator = prevGlobalFilter?.find(f => f.type === 'comparisonFilter')?.value.comparator;

                const newComparisonFilter = createComparisonFilter(prevComparator || defaultOperator.id, weight);
                return [...(globalFilterWithoutComparisonFilter || []), newComparisonFilter];
            });
        }, 300),
        []
    );

    function handleWeightChange(e: React.ChangeEvent<HTMLInputElement>) {
        const weight = e.target.value;
        setSearch(weight || '');

        updateFilterForWeight(Number(weight));
    }

    return (
        <Accordion defaultExpanded sx={{ boxShadow: 'none' }} disableGutters>
            <AccordionSummary sx={{ padding: 0 }} expandIcon={<ExpandMoreIcon />}>
                <Typography variant='h6' color={'text.secondary'}>{L.weight()}</Typography>
            </AccordionSummary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <Select
                            label={L.operator()}
                            list={COMPARISON_OPERATORS}
                            item={COMPARISON_OPERATORS.find(o => o.id === activeComparisonFilter?.value.comparator) || defaultOperator}
                            style={{ width: '4rem' }}
                            onChange={handleCategoryChange}
                        />
                        <TextField
                            variant='standard'
                            value={search}
                            type='number'
                            label={`${L.weight()} (${weightAbbreviation})`}
                            sx={{ maxWidth: '7rem' }}
                            onChange={handleWeightChange}
                            inputProps={{ min: 0 }}
                        />
                    </div>
                    <Button
                        variant='outlined'
                        size='small'
                        style={{ height: '100%' }}
                        onClick={() => {
                            setSearch('');
                            globalFilterRemover(table, 'comparisonFilter');
                        }}
                    >
                        {L.clear()}
                    </Button>
                </div>
            </div>
        </Accordion>
    );
};

function debounce<T>(func: (...args: T[]) => void, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return (...args: T[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    }
}